import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { PageHeader } from "../components/Layout";
import { toast } from "sonner";
import { RotateCw, Search, LayoutGrid, Trash2 } from "lucide-react";

const CATS = ["Baza", "Başlık", "Yatak", "Komodin", "Bench"];

// Kullanıcının belirlediği 5-durum renk haritası
// Bekliyor=Gri, Üretime Hazır=Mavi, Üretimde=Sarı, Hazır=Yeşil, Sevk Edildi=Koyu Mavi
const CELL_CLASS = {
  SIPARIS_ALINDI:  "bg-slate-100 text-slate-600 border-slate-400",
  URETIM_BEKLIYOR: "bg-blue-100 text-blue-900 border-blue-500",
  URETIMDE:        "bg-yellow-100 text-yellow-900 border-yellow-500",
  HAZIR:           "bg-emerald-100 text-emerald-900 border-emerald-600",
  SEVK_EDILDI:     "bg-blue-900 text-white border-blue-950",
  EMPTY:           "bg-slate-50 text-slate-300 border-slate-200",
};

const STATUS_LABEL = {
  SIPARIS_ALINDI: "Bekliyor",
  URETIM_BEKLIYOR: "Üretime Hazır",
  URETIMDE: "Üretimde",
  HAZIR: "Hazır",
  SEVK_EDILDI: "Sevk Edildi",
  EMPTY: "—",
};

export default function OrderStatusPanelPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders/status-matrix", { params: { include_archived: showArchived } });
      setRows(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  // Auto-refresh every 30s to reflect scan-driven updates
  useEffect(() => {
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [showArchived]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter(r =>
      r.order_number.toLowerCase().includes(s) ||
      (r.reference || "").toLowerCase().includes(s) ||
      (r.customer_name || "").toLowerCase().includes(s) ||
      Object.values(r.categories || {}).some(c => (c?.model || "").toLowerCase().includes(s))
    );
  }, [rows, q]);

  return (
    <div>
      <PageHeader
        title="Sipariş Durum Paneli"
        subtitle="Her sipariş tek satırda. Barkod okundukça hücreler otomatik renk değiştirir."
        actions={
          <button onClick={load} data-testid="panel-refresh-btn"
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-slate-800 hover:bg-slate-900 hover:text-white font-bold text-sm uppercase">
            <RotateCw size={16}/> Yenile
          </button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative max-w-md w-full sm:w-auto sm:min-w-[320px]">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="panel-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Sipariş / Referans / Müşteri / Model ara..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-slate-300 rounded-none focus:border-blue-700 focus:outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => setShowArchived(v => !v)}
          data-testid="panel-toggle-archived"
          className={`px-3 py-2 border-2 font-bold text-xs uppercase tracking-wide inline-flex items-center gap-1.5 ${
            showArchived
              ? "border-blue-900 bg-blue-900 text-white"
              : "border-slate-300 bg-white text-slate-600 hover:border-slate-500"
          }`}
        >
          <Trash2 size={14}/> {showArchived ? "Arşiv Görünüyor" : "Arşivi Göster"}
        </button>
        <Legend/>
      </div>

      <div className="card-industrial overflow-x-auto">
        <table className="w-full text-sm" data-testid="panel-table">
          <thead>
            <tr className="bg-slate-100 border-b-2 border-slate-300 text-xs uppercase tracking-widest text-slate-600">
              <th className="text-left px-3 py-3 font-bold">Sipariş</th>
              <th className="text-left px-3 py-3 font-bold">Müşteri</th>
              <th className="text-left px-3 py-3 font-bold">Referans</th>
              <th className="text-left px-3 py-3 font-bold">Ölçü</th>
              <th className="text-left px-3 py-3 font-bold">Kumaş</th>
              {CATS.map(c => (
                <th key={c} className="text-left px-2 py-3 font-bold">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5 + CATS.length} className="p-8 text-center text-slate-500">Yükleniyor...</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={5 + CATS.length} className="p-10 text-center text-slate-500">
                <LayoutGrid size={40} className="mx-auto mb-2 opacity-40" />
                Görüntülenecek sipariş yok.
              </td></tr>
            )}
            {filtered.map((r, i) => (
              <tr key={r.id} data-testid={`panel-row-${r.order_number}`}
                className={`border-b border-slate-200 ${i % 2 === 0 ? "bg-white" : "bg-slate-50"} hover:bg-blue-50`}>
                <td className="px-3 py-2">
                  <Link to={`/siparisler/${r.id}`} className="font-mono font-bold text-blue-700 hover:underline">
                    {r.order_number}
                  </Link>
                  {r.is_archived && (
                    <span className="ml-2 inline-block px-1.5 py-0.5 bg-blue-900 text-white text-[9px] font-bold tracking-widest uppercase align-middle">
                      Arşiv
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 font-semibold">{r.customer_name}</td>
                <td className="px-3 py-2 font-mono text-slate-600 text-xs">{r.reference || "—"}</td>
                <td className="px-3 py-2 font-mono">{r.size || "—"}</td>
                <td className="px-3 py-2 text-xs">{r.fabric || "—"}</td>
                {CATS.map(c => {
                  const cat = r.categories?.[c];
                  return <CategoryCell key={c} cat={cat} order={r.order_number} category={c} />;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CategoryCell({ cat, order, category }) {
  if (!cat || !cat.total) {
    return <td className={`px-2 py-2 border-l border-slate-100 text-center text-xs ${CELL_CLASS.EMPTY}`}>—</td>;
  }
  // Bottleneck view: en az ilerlemiş parça hücrenin rengini belirler.
  // Böylece "1'i bile Bekliyor'sa hücre Gri kalır" — üretim akışı için doğru sinyaldir.
  const counts = cat.status_counts || {};
  const total = cat.total;
  const waiting = counts.SIPARIS_ALINDI || 0;
  const readyForProd = counts.URETIM_BEKLIYOR || 0;
  const inProd = counts.URETIMDE || 0;
  const ready = (counts.HAZIR || 0) + (counts.SEVKIYATA_HAZIR || 0);
  const shipped = counts.SEVK_EDILDI || 0;

  let key;
  if (shipped === total) key = "SEVK_EDILDI";
  else if (waiting > 0) key = "SIPARIS_ALINDI";
  else if (readyForProd > 0) key = "URETIM_BEKLIYOR";
  else if (inProd > 0) key = "URETIMDE";
  else if (ready > 0) key = "HAZIR";
  else key = "SIPARIS_ALINDI";

  const doneCount = ready + shipped;

  return (
    <td className="px-1 py-2 border-l border-slate-100">
      <div
        data-testid={`panel-cell-${order}-${category}`}
        className={`px-2 py-1.5 border-2 ${CELL_CLASS[key]} text-xs font-bold leading-tight`}
        title={`${cat.model} · ${doneCount}/${total} hazır`}
      >
        <div className="font-display font-black text-[13px] truncate">{cat.model || "•"}</div>
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wide opacity-90 mt-0.5">
          <span>{STATUS_LABEL[key]}</span>
          <span className="font-mono tabular-nums">{doneCount}/{total}</span>
        </div>
      </div>
    </td>
  );
}

function Legend() {
  const items = [
    ["SIPARIS_ALINDI", "Bekliyor"],
    ["URETIM_BEKLIYOR", "Üretime Hazır"],
    ["URETIMDE", "Üretimde"],
    ["HAZIR", "Hazır"],
    ["SEVK_EDILDI", "Sevk Edildi"],
  ];
  return (
    <div className="flex flex-wrap gap-1.5 text-[10px]" data-testid="panel-legend">
      {items.map(([k, l]) => (
        <span key={k} className={`px-2 py-1 border-2 font-bold uppercase ${CELL_CLASS[k]}`}>{l}</span>
      ))}
    </div>
  );
}
